import { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Layers, TrendingUp, SprayCan, Leaf, Calculator, 
  Mic, Sparkles, AlertTriangle, CheckCircle, Info,
  Tractor, FileText, History
} from 'lucide-react';
import api from '../lib/api';

export default function FertilisationPage() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('suggestion');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // État pour suggestion IA
  const [suggestionForm, setSuggestionForm] = useState({
    objectif: 'fumure_croissance',
    culture: 'vigne',
    surface_ha: 10,
    mode_production: 'conventionnel',
    probleme_observe: ''
  });
  
  // État pour analyse compost
  const [compostForm, setCompostForm] = useState({
    type_compost: 'fumier_bovin',
    culture: 'lavandin',
    surface_ha: 25,
    quantite_disponible_tonnes: ''
  });
  
  const familles = [
    { id: 'fumure_fond', nom: 'Fumure de fond', icon: Layers, color: 'bg-amber-600', description: 'Enrichir le sol durablement' },
    { id: 'fumure_croissance', nom: 'Fumure croissance', icon: TrendingUp, color: 'bg-green-600', description: 'Faire pousser, stimuler' },
    { id: 'engrais_foliaire', nom: 'Engrais foliaires', icon: SprayCan, color: 'bg-blue-600', description: 'Corrections rapides' }
  ];
  
  const cultures = [
    'Vigne', 'Lavandin', 'Lavande', 'Olivier', 'Abricotier', 'Pommier',
    'Blé', 'Maïs', 'Tournesol', 'Colza', 'Tomate', 'Melon', 'Prairie'
  ];
  
  const typesCompost = [
    { id: 'vegetal', nom: 'Compost végétal' },
    { id: 'fumier_bovin', nom: 'Fumier bovin' },
    { id: 'fumier_ovin', nom: 'Fumier ovin' },
    { id: 'mixte', nom: 'Compost mixte' }
  ];
  
  const handleSuggestion = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/fertilisation/suggestion-ia', suggestionForm);
      setResult({ type: 'suggestion', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suggestion');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnalyseCompost = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...compostForm,
        quantite_disponible_tonnes: compostForm.quantite_disponible_tonnes ? 
          parseFloat(compostForm.quantite_disponible_tonnes) : null
      };
      const response = await api.post('/fertilisation/analyser-compost', payload);
      setResult({ type: 'compost', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };
  
  const openAssistantVocal = () => {
    // Ouvrir l'assistant vocal dans un nouvel onglet ou modal
    window.open('/assistant-ia', '_blank');
  };

  return (
    <div className="p-6 space-y-6" data-testid="fertilisation-page">
      {/* Header avec raccourci assistant */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Leaf className="h-8 w-8 text-green-600" />
            Fertilisation
          </h1>
          <p className="text-muted-foreground mt-1">
            Suggestions IA pour fumure de fond, croissance et foliaires
          </p>
        </div>
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={openAssistantVocal}
          data-testid="btn-assistant-vocal"
        >
          <Mic className="h-4 w-4" />
          Assistant vocal
        </Button>
      </div>
      
      {/* Les 3 familles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {familles.map((famille) => (
          <Card 
            key={famille.id}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              suggestionForm.objectif === famille.id ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => setSuggestionForm(prev => ({ ...prev, objectif: famille.id }))}
            data-testid={`famille-${famille.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${famille.color}`}>
                  <famille.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">{famille.nom}</h3>
                  <p className="text-sm text-muted-foreground">{famille.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="suggestion" className="gap-2">
            <Sparkles className="h-4 w-4" />
            Suggestion IA
          </TabsTrigger>
          <TabsTrigger value="compost" className="gap-2">
            <Tractor className="h-4 w-4" />
            Analyse Compost
          </TabsTrigger>
          <TabsTrigger value="historique" className="gap-2">
            <History className="h-4 w-4" />
            Historique
          </TabsTrigger>
        </TabsList>
        
        {/* TAB: Suggestion IA */}
        <TabsContent value="suggestion" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-500" />
                Suggestion de fertilisation
              </CardTitle>
              <CardDescription>
                L'IA vous propose une formule adaptée à votre objectif et votre culture
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Objectif</Label>
                  <Select 
                    value={suggestionForm.objectif} 
                    onValueChange={(v) => setSuggestionForm(prev => ({ ...prev, objectif: v }))}
                  >
                    <SelectTrigger data-testid="select-objectif">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {familles.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Culture</Label>
                  <Select 
                    value={suggestionForm.culture}
                    onValueChange={(v) => setSuggestionForm(prev => ({ ...prev, culture: v }))}
                  >
                    <SelectTrigger data-testid="select-culture">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cultures.map(c => (
                        <SelectItem key={c} value={c.toLowerCase()}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Surface (ha)</Label>
                  <Input 
                    type="number"
                    value={suggestionForm.surface_ha}
                    onChange={(e) => setSuggestionForm(prev => ({ ...prev, surface_ha: parseFloat(e.target.value) || 0 }))}
                    data-testid="input-surface"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Mode de production</Label>
                  <Select 
                    value={suggestionForm.mode_production}
                    onValueChange={(v) => setSuggestionForm(prev => ({ ...prev, mode_production: v }))}
                  >
                    <SelectTrigger data-testid="select-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conventionnel">Conventionnel</SelectItem>
                      <SelectItem value="bio">Agriculture Bio</SelectItem>
                      <SelectItem value="conversion">En conversion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {suggestionForm.objectif === 'engrais_foliaire' && (
                <div className="space-y-2">
                  <Label>Problème observé (optionnel)</Label>
                  <Input 
                    placeholder="Ex: chlorose, carence magnésium, stress hydrique..."
                    value={suggestionForm.probleme_observe}
                    onChange={(e) => setSuggestionForm(prev => ({ ...prev, probleme_observe: e.target.value }))}
                    data-testid="input-probleme"
                  />
                </div>
              )}
              
              <Button 
                onClick={handleSuggestion} 
                disabled={loading}
                className="w-full gap-2"
                data-testid="btn-suggerer"
              >
                <Sparkles className="h-4 w-4" />
                {loading ? 'Calcul en cours...' : 'Obtenir une suggestion IA'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* TAB: Analyse Compost */}
        <TabsContent value="compost" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tractor className="h-5 w-5 text-amber-600" />
                Analyse et préconisation compost
              </CardTitle>
              <CardDescription>
                Calculez la dose optimale et les apports pour votre compost
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Type de compost</Label>
                  <Select 
                    value={compostForm.type_compost}
                    onValueChange={(v) => setCompostForm(prev => ({ ...prev, type_compost: v }))}
                  >
                    <SelectTrigger data-testid="select-type-compost">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {typesCompost.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Culture cible</Label>
                  <Select 
                    value={compostForm.culture}
                    onValueChange={(v) => setCompostForm(prev => ({ ...prev, culture: v }))}
                  >
                    <SelectTrigger data-testid="select-culture-compost">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cultures.map(c => (
                        <SelectItem key={c} value={c.toLowerCase()}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Surface (ha)</Label>
                  <Input 
                    type="number"
                    value={compostForm.surface_ha}
                    onChange={(e) => setCompostForm(prev => ({ ...prev, surface_ha: parseFloat(e.target.value) || 0 }))}
                    data-testid="input-surface-compost"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Stock disponible (tonnes)</Label>
                  <Input 
                    type="number"
                    placeholder="Optionnel"
                    value={compostForm.quantite_disponible_tonnes}
                    onChange={(e) => setCompostForm(prev => ({ ...prev, quantite_disponible_tonnes: e.target.value }))}
                    data-testid="input-stock-compost"
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleAnalyseCompost} 
                disabled={loading}
                className="w-full gap-2"
                data-testid="btn-analyser-compost"
              >
                <Calculator className="h-4 w-4" />
                {loading ? 'Analyse en cours...' : 'Analyser et calculer'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* TAB: Historique */}
        <TabsContent value="historique">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Historique des apports
              </CardTitle>
              <CardDescription>
                Consultez l'historique de fertilisation par parcelle
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                Sélectionnez une parcelle dans le parcellaire pour voir son historique de fertilisation
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Erreur */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}
      
      {/* Résultat */}
      {result && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              {result.type === 'suggestion' ? 'Suggestion IA' : 'Analyse Compost'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.type === 'suggestion' && result.data && (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline">{result.data.objectif_label}</Badge>
                  <Badge variant="outline">{result.data.culture}</Badge>
                  <Badge variant="outline">{result.data.mode_production}</Badge>
                  <Badge variant="outline">{result.data.surface_ha} ha</Badge>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold">Produits recommandés :</h4>
                  {result.data.suggestions?.map((sugg, i) => (
                    <div key={i} className="p-4 bg-white rounded-lg border">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-medium">{sugg.nom}</h5>
                          <p className="text-2xl font-bold text-green-600">
                            {sugg.dose_preconisee} {sugg.unite}
                          </p>
                        </div>
                        {sugg.cout_estime && (
                          <Badge variant="secondary">~{sugg.cout_estime} €/ha</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">{sugg.justification}</p>
                      {sugg.periode_ideale && (
                        <p className="text-sm mt-1"><strong>Période :</strong> {sugg.periode_ideale}</p>
                      )}
                      {sugg.fractionnement && (
                        <div className="mt-2 text-sm">
                          <strong>Fractionnement conseillé :</strong>
                          <ul className="list-disc list-inside ml-2">
                            {sugg.fractionnement.map((f, j) => (
                              <li key={j}>{f.stade}: {f.dose} kg/ha</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {result.data.cout_total_surface > 0 && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-lg">
                      <strong>Coût estimé total :</strong> {result.data.cout_total_surface.toFixed(2)} € 
                      <span className="text-sm text-muted-foreground"> ({result.data.cout_total_ha.toFixed(2)} €/ha)</span>
                    </p>
                  </div>
                )}
                
                {result.data.alertes?.length > 0 && (
                  <div className="space-y-1">
                    {result.data.alertes.map((a, i) => (
                      <p key={i} className="text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> {a}
                      </p>
                    ))}
                  </div>
                )}
                
                {result.data.conseils?.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Info className="h-4 w-4" /> Conseils
                    </h4>
                    {result.data.conseils.map((c, i) => (
                      <p key={i} className="text-sm text-muted-foreground">• {c}</p>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {result.type === 'compost' && result.data && (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline">{result.data.type_compost}</Badge>
                  <Badge variant="outline">{result.data.culture}</Badge>
                  <Badge variant="outline">{result.data.surface_ha} ha</Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white rounded-lg border text-center">
                    <p className="text-sm text-muted-foreground">Dose recommandée</p>
                    <p className="text-3xl font-bold text-green-600">
                      {result.data.dose_recommandee?.tonnes_ha} t/ha
                    </p>
                    <p className="text-sm">{result.data.dose_recommandee?.kg_ha} kg/ha</p>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border text-center">
                    <p className="text-sm text-muted-foreground">Total nécessaire</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {result.data.dose_recommandee?.total_tonnes} t
                    </p>
                    <p className="text-sm">pour {result.data.surface_ha} ha</p>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border text-center">
                    <p className="text-sm text-muted-foreground">Valeur agronomique</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {result.data.valeur_agronomique_estimee}
                    </p>
                  </div>
                </div>
                
                {result.data.message_stock && (
                  <div className={`p-3 rounded-lg ${result.data.stock_suffisant ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <p>{result.data.message_stock}</p>
                  </div>
                )}
                
                <div className="p-4 bg-white rounded-lg border">
                  <h4 className="font-semibold mb-2">Apports effectifs (unités/ha)</h4>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">N total</p>
                      <p className="font-bold">{result.data.apports_effectifs_unites_ha?.N_total}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">N dispo an 1</p>
                      <p className="font-bold text-green-600">{result.data.apports_effectifs_unites_ha?.N_disponible_an1}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">P2O5</p>
                      <p className="font-bold">{result.data.apports_effectifs_unites_ha?.P2O5}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">K2O</p>
                      <p className="font-bold">{result.data.apports_effectifs_unites_ha?.K2O}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">MO</p>
                      <p className="font-bold">{result.data.apports_effectifs_unites_ha?.MO}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-white rounded-lg border">
                  <h4 className="font-semibold mb-2">Couverture des besoins</h4>
                  <div className="flex gap-4">
                    <Badge variant="outline">N: {result.data.couverture_besoins?.N}</Badge>
                    <Badge variant="outline">P2O5: {result.data.couverture_besoins?.P2O5}</Badge>
                    <Badge variant="outline">K2O: {result.data.couverture_besoins?.K2O}</Badge>
                  </div>
                </div>
                
                {result.data.alertes?.length > 0 && (
                  <div className="space-y-1">
                    {result.data.alertes.map((a, i) => (
                      <p key={i} className="text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> {a}
                      </p>
                    ))}
                  </div>
                )}
                
                {result.data.conseils?.length > 0 && (
                  <div className="space-y-1 mt-4">
                    <h4 className="font-semibold">Conseils :</h4>
                    {result.data.conseils.map((c, i) => (
                      <p key={i} className="text-sm">• {c}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
